import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { HelpDot } from '../components/HelpDot';
import { campaignsAPI, leadsAPI } from '../lib/api';
import api from '../lib/api';
import { RecordingPlayer } from '../components/CallLogCard';
import CampaignsTabs from '../components/campaigns/CampaignsTabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Play, Pause, Trash2, Sparkles, Phone, Target, ArrowLeft, Users, Clock, CheckCircle2, XCircle, Voicemail, Search, ChevronRight, ChevronDown, Zap, BarChart3, MessageCircle, Layers, Download, FileText, Mail, RefreshCw, Eye, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300',
  activating: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
};

const callStatusColors = {
  scheduled: 'text-blue-600 dark:text-blue-400',
  pending: 'text-slate-500',
  processing: 'text-amber-500',
  completed: 'text-emerald-600 dark:text-emerald-400',
  failed: 'text-red-500',
  paused: 'text-amber-600',
};

const callTypeLabels = {
  discovery: 'Discovery',
  demo_scheduling: 'Demo Scheduling',
  follow_up: 'Follow-Up',
  closing: 'Closing',
};

// Channel identity: icon + label + colour so the campaigns list clearly shows
// whether a campaign is Voice / Email / WhatsApp (was previously ambiguous —
// only the voice-only call_type was shown for every channel).
const channelMeta = {
  voice: { label: 'Voice', Icon: Phone, cls: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300' },
  email: { label: 'Email', Icon: Mail, cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300' },
  whatsapp: { label: 'WhatsApp', Icon: MessageCircle, cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' },
};

function ChannelBadge({ channel }) {
  const meta = channelMeta[channel] || channelMeta.voice;
  const Icon = meta.Icon;
  return (
    <Badge className={meta.cls} data-testid={`campaign-channel-${channel || 'voice'}`}>
      <Icon className="h-3 w-3 mr-1" />{meta.label}
    </Badge>
  );
}

const LEAD_STATUSES = ['New Lead', 'Contacted', 'Qualified', 'Proposal Sent', 'Negotiation', 'Won', 'Lost'];

function DownloadRecordingButton({ url, callId }) {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    if (!url) return;
    setBusy(true);
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      const objUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${callId || 'call'}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      toast.error('Recording not available yet');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={download}
      disabled={busy}
      className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
      data-testid={`download-recording-${callId || ''}`}
      title="Download recording"
    >
      <Download className="h-3.5 w-3.5" /> {busy ? 'Downloading…' : 'Download'}
    </button>
  );
}

export default function Campaigns() {  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [campaignLeads, setCampaignLeads] = useState([]);
  const [campaignRecipients, setCampaignRecipients] = useState(null);
  const [expandedRecipient, setExpandedRecipient] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [waTemplates, setWaTemplates] = useState([]);
  const [waLoading, setWaLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplateStats, setEmailTemplateStats] = useState({});
  const [audienceBatches, setAudienceBatches] = useState([]);
  const [form, setForm] = useState({
    name: '', channel: 'voice', call_type: 'discovery', max_calls_per_day: 20, priority: 5, notes: '',
    email_subject: '', email_body: '', emails_per_day: 50,
    wa_template_name: '', wa_template_language: 'en_US', wa_params_needed: 0, messages_per_day: 250,
    target_filters: { min_icp_score: 0, statuses: [], import_batch: '' },
    target_market: '', industry: '',
  });

  const loadWaTemplates = useCallback(async () => {
    setWaLoading(true);
    try {
      const { data } = await campaignsAPI.getWhatsappTemplates();
      setWaTemplates(data.templates || []);
      if (!(data.templates || []).length) {
        toast.info('No approved WhatsApp templates found. Connect WhatsApp and get a template approved in Meta.');
      }
    } catch {
      setWaTemplates([]);
    } finally {
      setWaLoading(false);
    }
  }, []);

  const loadEmailTemplates = useCallback(async () => {
    try {
      const { data } = await campaignsAPI.getEmailTemplates();
      setEmailTemplates(data.templates || []);
    } catch {
      setEmailTemplates([]);
    }
    try {
      const { data } = await campaignsAPI.templateAnalytics();
      const map = {};
      (data.templates || []).forEach((t) => {
        if (t.category === 'email' && t.stats) {
          map[t.template_id] = { open_rate: t.stats.open_rate || 0, reply_rate: t.stats.reply_rate || 0, sent: t.stats.sent || 0 };
        }
      });
      setEmailTemplateStats(map);
    } catch {
      setEmailTemplateStats({});
    }
  }, []);

  useEffect(() => { loadEmailTemplates(); }, [loadEmailTemplates]);

  const loadAudienceBatches = useCallback(async () => {
    try {
      const { data } = await leadsAPI.getAudience({ limit: 1 });
      setAudienceBatches(data.batches || []);
    } catch { setAudienceBatches([]); }
  }, []);

  useEffect(() => { loadAudienceBatches(); }, [loadAudienceBatches]);

  const applyEmailTemplate = (templateId) => {
    const t = emailTemplates.find((x) => x.id === templateId);
    if (!t) return;
    const c = t.content || {};
    const norm = (s) => (s || '').replace(/\{\{\s*/g, '{').replace(/\s*\}\}/g, '}');
    setForm((f) => ({ ...f, email_subject: norm(c.subject), email_body: norm(c.body) }));
    toast.success(`Loaded "${t.name}" template`);
  };

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const { data } = await campaignsAPI.getAll(params);
      setCampaigns(data.campaigns || []);
    } catch (err) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Launch-from-Audience deep-link: /campaigns?open=1&channel=<voice|email|whatsapp>&batch=<name>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('open') === '1') {
      const batch = searchParams.get('batch') || '';
      const ch = searchParams.get('channel');
      const channel = ['voice', 'email', 'whatsapp'].includes(ch) ? ch : 'voice';
      if (channel === 'whatsapp') loadWaTemplates();
      setForm((f) => ({
        ...f,
        channel,
        name: batch ? `${batch} outreach` : f.name,
        target_filters: { ...f.target_filters, import_batch: batch },
      }));
      setShowCreate(true);
      if (batch) toast.info(`Targeting only your "${batch}" list`);
      searchParams.delete('open'); searchParams.delete('channel'); searchParams.delete('batch');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadWaTemplates]);

  const openDetail = async (campaign) => {
    setSelectedCampaign(campaign.id);
    if (routeId !== campaign.id) navigate(`/campaigns/view/${campaign.id}`);
    setDetailLoading(true);
    try {
      const [detailRes, leadsRes] = await Promise.all([
        campaignsAPI.getOne(campaign.id),
        campaignsAPI.getLeads(campaign.id),
      ]);
      setCampaignDetail(detailRes.data);
      setCampaignLeads(leadsRes.data.leads || []);
      campaignsAPI.getRecipients(campaign.id)
        .then((r) => setCampaignRecipients(r.data))
        .catch(() => setCampaignRecipients(null));
    } catch (err) {
      toast.error('Failed to load campaign details');
      setSelectedCampaign(null);
      navigate('/campaigns');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedCampaign(null);
    setCampaignDetail(null);
    setCampaignLeads([]);
    setCampaignRecipients(null);
    setExpandedRecipient(null);
    if (routeId) navigate('/campaigns');
  };

  // Deep link: open a campaign directly from /campaigns/view/:id
  useEffect(() => {
    if (routeId && selectedCampaign !== routeId) {
      openDetail({ id: routeId });
    }
  }, [routeId]);  

  const handleExportResults = async (id, name) => {
    setExporting(true);
    try {
      const res = await campaignsAPI.exportResults(id);
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(name || 'campaign').replace(/[^A-Za-z0-9_-]+/g, '_')}_results.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('Results exported');
    } catch (err) {
      toast.error('Failed to export results');
    } finally {
      setExporting(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Campaign name is required'); return; }
    try {
      await campaignsAPI.create(form);
      toast.success('Campaign created');
      setShowCreate(false);
      setPreviewCount(null);
      setForm({ name: '', channel: 'voice', call_type: 'discovery', max_calls_per_day: 20, priority: 5, notes: '', email_subject: '', email_body: '', emails_per_day: 50, wa_template_name: '', wa_template_language: 'en_US', wa_params_needed: 0, messages_per_day: 250, target_filters: { min_icp_score: 0, statuses: [], import_batch: '' }, target_market: '', industry: '' });
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const handleActivate = async (id) => {
    if (activatingId) return;  // in-flight guard — block double-click / parallel activate
    setActivatingId(id);
    try {
      const { data } = await campaignsAPI.activate(id);
      if (data.reason === 'already_activating') {
        toast.info('This campaign is already being activated — please wait.');
      } else {
        toast.success(`Campaign activated — ${data.scheduled_calls || 0} calls scheduled`);
      }
      fetchCampaigns();
      if (selectedCampaign === id) openDetail({ id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate');
    } finally {
      setActivatingId(null);
    }
  };

  const handlePause = async (id) => {
    try {
      await campaignsAPI.pause(id);
      toast.success('Campaign paused');
      fetchCampaigns();
      if (selectedCampaign === id) openDetail({ id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pause');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign? This cannot be undone.')) return;
    try {
      await campaignsAPI.delete(id);
      toast.success('Campaign deleted');
      if (selectedCampaign === id) closeDetail();
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRetryFailed = async (id) => {
    try {
      const { data } = await campaignsAPI.retryFailed(id);
      toast.success(`Requeued ${data.requeued || 0} failed recipient(s)`);
      fetchCampaigns();
      if (selectedCampaign === id) openDetail({ id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry');
    }
  };

  const handleAutoCreate = async () => {
    try {
      const { data } = await campaignsAPI.autoCreate();
      if (data.created > 0) {
        toast.success(`AI created ${data.created} campaign(s)`);
        fetchCampaigns();
      } else {
        toast.info('No campaigns needed — not enough qualifying leads');
      }
    } catch (err) {
      toast.error('Auto-create failed');
    }
  };

  const handlePreviewLeads = async () => {
    try {
      const { data } = await campaignsAPI.previewLeads({
        min_icp_score: form.target_filters.min_icp_score || 0,
        statuses: form.target_filters.statuses || [],
        channel: form.channel,
        import_batch: form.target_filters.import_batch || undefined,
      });
      setPreviewCount(data.matching_leads);
    } catch {
      setPreviewCount(null);
    }
  };

  const toggleStatus = (status) => {
    const current = form.target_filters.statuses || [];
    const updated = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    setForm({ ...form, target_filters: { ...form.target_filters, statuses: updated } });
  };

  const filteredCampaigns = campaigns.filter(c =>
    !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const summaryStats = {
    active: campaigns.filter(c => c.status === 'active').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    paused: campaigns.filter(c => c.status === 'paused').length,
    totalLeads: campaigns.reduce((sum, c) => sum + (c.matching_leads || 0), 0),
  };

  // DETAIL VIEW
  if (selectedCampaign && campaignDetail) {
    const d = campaignDetail;
    const totalCalls = (d.scheduled_calls || 0) + (d.calls_completed || 0) + (d.calls_failed || 0);
    const completionPct = totalCalls > 0 ? Math.round(((d.calls_completed || 0) / totalCalls) * 100) : 0;

    return (
      <div className="space-y-6" data-testid="campaign-detail-view">
        <button onClick={closeDetail} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-list-btn">
          <ArrowLeft className="h-4 w-4" /> Back to Campaigns
        </button>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="campaign-detail-title">{d.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={statusColors[d.status]}>{d.status}</Badge>
              <ChannelBadge channel={d.channel} />
              {(d.channel || 'voice') === 'voice' && <Badge variant="outline">{callTypeLabels[d.call_type] || d.call_type}</Badge>}
              <span className="text-sm text-muted-foreground">Priority: {d.priority}</span>
              <span className="text-sm text-muted-foreground">Created {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</span>
            </div>
            {d.notes && <p className="text-sm text-muted-foreground mt-2">{d.notes}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExportResults(d.id, d.name)} disabled={exporting} data-testid="export-results-btn">
              <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            {d.status === 'draft' && <Button disabled={activatingId === d.id} onClick={() => handleActivate(d.id)} data-testid="detail-activate-btn"><Play className="h-4 w-4 mr-2" /> {activatingId === d.id ? 'Activating…' : 'Activate'}</Button>}
            {d.status === 'active' && <Button variant="outline" onClick={() => handlePause(d.id)} data-testid="detail-pause-btn"><Pause className="h-4 w-4 mr-2" /> Pause</Button>}
            {d.status === 'paused' && <Button disabled={activatingId === d.id} onClick={() => handleActivate(d.id)} data-testid="detail-resume-btn"><Play className="h-4 w-4 mr-2" /> {activatingId === d.id ? 'Resuming…' : 'Resume'}</Button>}
            {((d.wa_failed || 0) > 0 || (d.email_failed || 0) > 0 || (d.calls_failed || 0) > 0) && (
              <Button variant="outline" onClick={() => handleRetryFailed(d.id)} data-testid="detail-retry-failed-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Retry failed
              </Button>
            )}
            {d.status !== 'active' && <Button variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(d.id)} data-testid="detail-delete-btn"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>}
          </div>
        </div>

        {/* Stats Cards — channel-aware (voice shows calls; WhatsApp/email show sends) */}
        {(d.channel || 'voice') === 'whatsapp' ? (
          <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="campaign-stats">
            <StatCard icon={<Target className="h-5 w-5 text-blue-500" />} label="Queued Total" value={d.queued_messages || ((d.wa_sent || 0) + (d.wa_failed || 0) + (d.pending_calls || 0) + (d.wa_skipped || 0))} />
            <StatCard icon={<Clock className="h-5 w-5 text-amber-500" />} label="Pending" value={d.pending_calls || 0} />
            <StatCard icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />} label="Accepted by Meta" value={d.wa_sent || 0} />
            <StatCard icon={<XCircle className="h-5 w-5 text-red-500" />} label="Send Failed" value={d.wa_failed || 0} />
            <StatCard icon={<Voicemail className="h-5 w-5 text-purple-500" />} label="Skipped" value={d.wa_skipped || 0} />
          </div>
          {/* F2: TRUE delivery funnel reconciled from Meta delivery-status webhooks */}
          {d.wa_delivery && (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground mb-2" data-testid="wa-delivery-heading">
                Actual delivery (from WhatsApp read receipts){d.wa_delivery.reconciled === false ? ' · estimated from message history' : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="wa-delivery-stats">
                <StatCard icon={<CheckCheck className="h-5 w-5 text-emerald-500" />} label="Delivered" value={d.wa_delivery.delivered || 0} />
                <StatCard icon={<Eye className="h-5 w-5 text-sky-500" />} label="Read" value={d.wa_delivery.read || 0} />
                <StatCard icon={<XCircle className="h-5 w-5 text-red-500" />} label="Failed / not delivered" value={d.wa_delivery.failed || 0} />
                <StatCard icon={<Clock className="h-5 w-5 text-amber-500" />} label="Awaiting receipt" value={d.wa_delivery.pending || 0} />
              </div>
              {(d.wa_delivery.withheld_131049 || 0) > 0 && (
                <div className="mt-2 text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2" data-testid="wa-withheld-131049-note">
                  {d.wa_delivery.withheld_131049} message{(d.wa_delivery.withheld_131049 || 0) === 1 ? ' was' : 's were'} withheld by Meta (error 131049 — per-user marketing limit). These were not delivered and were auto-refunded. Try a UTILITY template or space out re-sends.
                </div>
              )}
            </div>
          )}
          </>
        ) : (d.channel || 'voice') === 'email' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="campaign-stats">
            <StatCard icon={<Target className="h-5 w-5 text-blue-500" />} label="Queued Total" value={(d.email_sent || 0) + (d.email_failed || 0) + (d.pending_calls || 0)} />
            <StatCard icon={<Clock className="h-5 w-5 text-amber-500" />} label="Pending" value={d.pending_calls || 0} />
            <StatCard icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />} label="Sent" value={d.email_sent || 0} />
            <StatCard icon={<XCircle className="h-5 w-5 text-red-500" />} label="Failed" value={d.email_failed || 0} />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="campaign-stats">
            <StatCard icon={<Target className="h-5 w-5 text-blue-500" />} label="Matching Leads" value={d.matching_leads || 0} />
            <StatCard icon={<Clock className="h-5 w-5 text-amber-500" />} label="Scheduled" value={d.scheduled_calls || 0} />
            <StatCard icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />} label="Completed" value={d.calls_completed || 0} />
            <StatCard icon={<XCircle className="h-5 w-5 text-red-500" />} label="Failed" value={d.calls_failed || 0} />
            <StatCard icon={<Voicemail className="h-5 w-5 text-purple-500" />} label="Voicemail" value={d.calls_voicemail || 0} />
          </div>
        )}
        {d.pause_reason === 'no_credits' && d.status === 'paused' && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700" data-testid="campaign-no-credits-banner">
            Paused automatically — out of credits. Top up your balance, then Resume to continue sending.
          </div>
        )}

        {/* Progress */}
        <div className="border rounded-lg p-4" data-testid="campaign-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Campaign Progress</span>
            <span className="text-sm text-muted-foreground">{completionPct}%</span>
          </div>
          <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${completionPct}%` }} />
          </div>
          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
            <span>Max {(d.channel || 'voice') === 'whatsapp' ? `${d.messages_per_day || 250} messages/day` : (d.channel || 'voice') === 'email' ? `${d.emails_per_day || 50} emails/day` : `${d.max_calls_per_day} calls/day`}</span>
            <span>{d.pending_calls || 0} pending</span>
            <span>{d.in_progress_calls || 0} in progress</span>
          </div>
        </div>

        {/* Recipients — who actually got the message */}
        {campaignRecipients && campaignRecipients.total > 0 && (
          <div className="border rounded-lg p-4" data-testid="campaign-recipients">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Recipients ({campaignRecipients.total})</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(campaignRecipients.counts || {}).map(([st, n]) => (
                  <Badge key={st} variant="outline" className="capitalize" data-testid={`recipient-count-${st}`}>{st}: {n}</Badge>
                ))}
              </div>
            </div>
            <div className="max-h-80 overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Recipient</th>
                    <th className="px-3 py-2">{campaignRecipients.channel === 'email' ? 'Email' : 'Phone'}</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">When</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignRecipients.recipients.map((r, i) => {
                    const call = r.call;
                    const msgs = r.messages;
                    const hasPlayback = !!(call && (call.recording_available || call.transcript || call.ai_summary)) || (msgs && msgs.length > 0);
                    const isOpen = expandedRecipient === i;
                    return (
                    <React.Fragment key={i}>
                    <tr className="border-t" data-testid={`recipient-row-${i}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {hasPlayback && (
                            <button
                              onClick={() => setExpandedRecipient(isOpen ? null : i)}
                              className="text-muted-foreground hover:text-foreground transition-colors"
                              data-testid={`recipient-expand-${i}`}
                              title="View call recording & transcript"
                            >
                              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                          {r.name || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{campaignRecipients.channel === 'email' ? (r.email || '—') : (r.phone || '—')}</td>
                      <td className="px-3 py-2">
                        <Badge className={
                          r.status === 'sent' || r.status === 'completed' ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                          : r.status === 'failed' ? 'bg-red-500/15 text-red-600 border-red-500/30'
                          : r.status === 'skipped' || r.status === 'dead_letter' ? 'bg-amber-500/15 text-amber-600 border-amber-500/30'
                          : 'bg-slate-500/15 text-slate-600 border-slate-500/30'
                        }>{r.status}</Badge>
                        {r.note && <span className="ml-2 text-xs text-muted-foreground">{r.note}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.sent_at ? new Date(r.sent_at).toLocaleString() : (r.scheduled_at ? `Scheduled: ${new Date(r.scheduled_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}` : '—')}</td>
                    </tr>
                    {isOpen && hasPlayback && (
                      <tr className="border-t bg-muted/20" data-testid={`recipient-playback-${i}`}>
                        <td colSpan={4} className="px-4 py-3">
                          <div className="space-y-3">
                            {call && call.recording_available && call.recording_url && (
                              <div className="flex items-center gap-3">
                                <RecordingPlayer url={call.recording_url} />
                                {call.duration_seconds ? <span className="text-xs text-muted-foreground">{call.duration_seconds}s</span> : null}
                                {call.outcome ? <Badge variant="outline" className="capitalize text-xs">{call.outcome}</Badge> : null}
                                <DownloadRecordingButton url={call.recording_url} callId={call.call_id} />
                              </div>
                            )}
                            {call && call.ai_summary && (
                              <div className="text-sm">
                                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1"><Sparkles className="h-3.5 w-3.5" /> AI Summary</div>
                                <p className="text-foreground/90">{call.ai_summary}</p>
                              </div>
                            )}
                            {call && call.transcript && (
                              <details className="text-sm">
                                <summary className="cursor-pointer flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><FileText className="h-3.5 w-3.5" /> Transcript</summary>
                                <pre className="mt-2 whitespace-pre-wrap text-xs text-foreground/80 max-h-48 overflow-auto bg-background rounded p-2 border">{call.transcript}</pre>
                              </details>
                            )}
                            {msgs && msgs.length > 0 && (
                              <div className="space-y-2" data-testid={`recipient-thread-${i}`}>
                                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><MessageCircle className="h-3.5 w-3.5" /> Message thread ({msgs.length})</div>
                                {msgs.map((m, mi) => (
                                  <div key={mi} className={`rounded p-2 border text-xs ${m.direction === 'inbound' ? 'bg-primary/5 ml-6' : 'bg-background mr-6'}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge variant="outline" className="text-[10px] capitalize">{m.direction || 'outbound'}</Badge>
                                      {m.subject ? <span className="font-medium">{m.subject}</span> : null}
                                      {m.at ? <span className="text-muted-foreground ml-auto">{new Date(m.at).toLocaleString()}</span> : null}
                                    </div>
                                    <p className="whitespace-pre-wrap text-foreground/80">{m.body || '(no content)'}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Configuration */}
        {d.target_filters && Object.keys(d.target_filters).length > 0 && (
          <div className="border rounded-lg p-4" data-testid="campaign-filters">
            <h3 className="text-sm font-medium mb-2">Target Filters</h3>
            <div className="flex flex-wrap gap-2">
              {d.target_filters.min_icp_score > 0 && <Badge variant="outline">ICP Score &ge; {d.target_filters.min_icp_score}</Badge>}
              {d.target_filters.statuses?.map(s => <Badge key={s} variant="outline">{s}</Badge>)}
              {d.target_filters.has_phone && <Badge variant="outline">Has Phone</Badge>}
            </div>
          </div>
        )}

        {/* Lead List */}
        <div className="border rounded-lg overflow-hidden" data-testid="campaign-leads-table">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" /> Campaign Leads ({campaignLeads.length})</h3>
          </div>
          {detailLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading leads...</div>
          ) : campaignLeads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {d.status === 'draft' ? 'Activate the campaign to schedule calls for matching leads' : 'No leads assigned yet'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Lead Status</TableHead>
                  <TableHead className="text-center">ICP Score</TableHead>
                  <TableHead>Call Status</TableHead>
                  <TableHead>Scheduled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaignLeads.map((lead, i) => (
                  <TableRow key={lead.lead_id || i}>
                    <TableCell>
                      <div className="font-medium">{lead.name}</div>
                      <div className="text-xs text-muted-foreground">{lead.email}</div>
                    </TableCell>
                    <TableCell className="text-sm">{lead.company || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{lead.lead_status || '—'}</Badge></TableCell>
                    <TableCell className="text-center">
                      {lead.icp_score > 0 ? (
                        <span className={`text-sm font-medium ${lead.icp_score >= 80 ? 'text-emerald-600' : lead.icp_score >= 50 ? 'text-amber-600' : 'text-slate-500'}`}>{lead.icp_score}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`text-sm font-medium capitalize ${callStatusColors[lead.call_status] || 'text-slate-500'}`}>{lead.call_status}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {lead.scheduled_at ? new Date(lead.scheduled_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <CampaignsTabs />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="campaigns-title">
            Campaigns
            <HelpDot guide="run-a-cadence" label="How campaigns work" />
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage outbound call campaigns for your leads</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAutoCreate} data-testid="auto-create-btn">
            <Sparkles className="h-4 w-4 mr-2" /> AI Auto-Create
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="create-campaign-btn">
            <Plus className="h-4 w-4 mr-2" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="campaigns-summary">
          <MiniStat icon={<Zap className="h-4 w-4 text-emerald-500" />} label="Active" value={summaryStats.active} />
          <MiniStat icon={<Clock className="h-4 w-4 text-amber-500" />} label="Paused" value={summaryStats.paused} />
          <MiniStat icon={<BarChart3 className="h-4 w-4 text-slate-500" />} label="Draft" value={summaryStats.draft} />
          <MiniStat icon={<Target className="h-4 w-4 text-blue-500" />} label="Total Leads Targeted" value={summaryStats.totalLeads} />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-campaigns"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filteredCampaigns.length} campaign(s)</span>
      </div>

      {/* Campaign Table */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading campaigns...</div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-lg" data-testid="campaigns-empty">
          <Phone className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="text-lg font-medium">No campaigns yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Create your first outbound call campaign or let AI suggest one.</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={handleAutoCreate}><Sparkles className="h-4 w-4 mr-2" /> AI Auto-Create</Button>
            <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-2" /> New Campaign</Button>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Leads</TableHead>
                <TableHead className="text-center">Scheduled</TableHead>
                <TableHead className="text-center">Calls/Day</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.map((c) => {
                const totalTarget = (c.matching_leads || 0);
                const completed = (c.calls_completed || 0);
                const progressPct = totalTarget > 0 ? Math.round((completed / totalTarget) * 100) : 0;
                return (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(c)} data-testid={`campaign-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-medium flex items-center gap-1">{c.name} <ChevronRight className="h-3 w-3 text-muted-foreground" /></div>
                          {c.notes && <div className="text-xs text-muted-foreground truncate max-w-[250px]">{c.notes}</div>}
                          {/* Inline progress bar */}
                          {c.status !== 'draft' && (
                            <div className="w-24 h-1 bg-muted rounded-full mt-1 overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${progressPct}%` }} />
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><ChannelBadge channel={c.channel} /></TableCell>
                    <TableCell>{(c.channel || 'voice') === 'voice' ? <Badge variant="outline">{callTypeLabels[c.call_type] || c.call_type}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell><Badge className={statusColors[c.status]}>{c.status}</Badge></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Target className="h-3 w-3 text-muted-foreground" />{totalTarget}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{c.scheduled_calls || 0}</TableCell>
                    <TableCell className="text-center">{c.max_calls_per_day}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        {c.status === 'draft' && <Button size="sm" variant="outline" disabled={activatingId === c.id} onClick={() => handleActivate(c.id)} data-testid={`activate-${c.id}`}><Play className="h-3 w-3 mr-1" /> {activatingId === c.id ? 'Activating…' : 'Activate'}</Button>}
                        {c.status === 'active' && <Button size="sm" variant="outline" onClick={() => handlePause(c.id)} data-testid={`pause-${c.id}`}><Pause className="h-3 w-3 mr-1" /> Pause</Button>}
                        {c.status === 'paused' && <Button size="sm" variant="outline" disabled={activatingId === c.id} onClick={() => handleActivate(c.id)} data-testid={`resume-${c.id}`}><Play className="h-3 w-3 mr-1" /> {activatingId === c.id ? 'Resuming…' : 'Resume'}</Button>}
                        {c.status !== 'active' && <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} data-testid={`delete-${c.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) setPreviewCount(null); }}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto" data-testid="create-campaign-dialog">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription>
              Target an audience list or your pipeline and launch a voice, email, or WhatsApp campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Q1 Discovery Blitz" data-testid="campaign-name-input" />
            </div>

            <div data-testid="campaign-audience-scope">
              <Label>Audience list</Label>
              <select
                value={form.target_filters.import_batch || ''}
                onChange={(e) => setForm({ ...form, target_filters: { ...form.target_filters, import_batch: e.target.value } })}
                className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                data-testid="campaign-audience-select"
              >
                <option value="">All eligible leads (filter by status / ICP below)</option>
                {form.target_filters.import_batch && !audienceBatches.some((b) => b.name === form.target_filters.import_batch) && (
                  <option value={form.target_filters.import_batch}>{form.target_filters.import_batch}</option>
                )}
                {audienceBatches.map((b) => (
                  <option key={b.name} value={b.name}>{b.name} ({b.count})</option>
                ))}
              </select>
              {form.target_filters.import_batch ? (
                <p className="text-xs text-cyan-400 mt-1 flex items-center gap-1.5" data-testid="campaign-batch-scope">
                  <Layers className="h-3.5 w-3.5" /> Targeting only your list: <strong>{form.target_filters.import_batch}</strong>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Pick a specific uploaded list, or leave as “All” to target by status / ICP.</p>
              )}
            </div>

            {/* Channel picker — Phase 3 */}
            <div>
              <Label>Channel</Label>
              <div className="inline-flex rounded-lg border border-border overflow-hidden w-full mt-1" data-testid="campaign-channel-toggle">
                <button type="button" onClick={() => setForm({ ...form, channel: 'voice' })}
                  className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.channel === 'voice' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted/50'}`}
                  data-testid="campaign-channel-voice">
                  <Phone className="h-4 w-4" /> Voice Call
                </button>
                <button type="button" onClick={() => setForm({ ...form, channel: 'email' })}
                  className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 border-l border-border transition-colors ${form.channel === 'email' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted/50'}`}
                  data-testid="campaign-channel-email">
                  <Sparkles className="h-4 w-4" /> Email
                </button>
                <button type="button" onClick={() => { setForm({ ...form, channel: 'whatsapp' }); loadWaTemplates(); }}
                  className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 border-l border-border transition-colors ${form.channel === 'whatsapp' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted/50'}`}
                  data-testid="campaign-channel-whatsapp">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </button>
              </div>
            </div>

            {form.channel === 'voice' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Call Type</Label>
                <Select value={form.call_type} onValueChange={(v) => setForm({ ...form, call_type: v })}>
                  <SelectTrigger data-testid="campaign-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discovery">Discovery</SelectItem>
                    <SelectItem value="demo_scheduling">Demo Scheduling</SelectItem>
                    <SelectItem value="follow_up">Follow-Up</SelectItem>
                    <SelectItem value="closing">Closing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Calls/Day</Label>
                <Input type="number" min={1} max={100} value={form.max_calls_per_day} onChange={(e) => setForm({ ...form, max_calls_per_day: parseInt(e.target.value) || 20 })} data-testid="campaign-max-calls-input" />
              </div>
            </div>
            )}
            {form.channel === 'email' && (
            <div className="space-y-3" data-testid="campaign-email-fields">
              {emailTemplates.length > 0 && (
              <div>
                <Label>Start from a template (optional)</Label>
                <Select value="" onValueChange={applyEmailTemplate}>
                  <SelectTrigger data-testid="campaign-email-template-select"><SelectValue placeholder="Pick a saved email template…" /></SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map((t) => {
                      const s = emailTemplateStats[t.id];
                      return (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                          {s && s.sent > 0 && (
                            <span className="text-xs text-muted-foreground ml-1.5">· {s.open_rate}% open · {s.reply_rate}% reply</span>
                          )}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Auto-fills the subject &amp; body below — edit freely before launching.</p>
              </div>
              )}
              <div>
                <Label>Email Subject *</Label>
                <Input value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} placeholder="e.g., Quick question, {first_name}" data-testid="campaign-email-subject-input" />
              </div>
              <div>
                <Label>Email Body *</Label>
                <Textarea value={form.email_body} onChange={(e) => setForm({ ...form, email_body: e.target.value })} rows={5} placeholder="Hi {name}, we help {company}..." data-testid="campaign-email-body-input" />
                <p className="text-xs text-muted-foreground mt-1">Placeholders: <code>{'{name}'}</code> <code>{'{first_name}'}</code> <code>{'{company}'}</code>. A one-click unsubscribe link is added automatically.</p>
              </div>
              <div>
                <Label>Emails/Day (pacing)</Label>
                <Input type="number" min={1} max={2000} value={form.emails_per_day} onChange={(e) => setForm({ ...form, emails_per_day: parseInt(e.target.value) || 50 })} data-testid="campaign-emails-per-day-input" />
              </div>
            </div>
            )}
            {form.channel === 'whatsapp' && (
            <div className="space-y-3" data-testid="campaign-whatsapp-fields">
              <div>
                <Label>Approved Template *</Label>
                <Select
                  value={form.wa_template_name}
                  onValueChange={(v) => {
                    const t = waTemplates.find((x) => x.name === v);
                    setForm({ ...form, wa_template_name: v, wa_template_language: t?.language || 'en_US', wa_params_needed: t?.params_needed || 0 });
                  }}
                >
                  <SelectTrigger data-testid="campaign-wa-template-select">
                    <SelectValue placeholder={waLoading ? 'Loading templates…' : 'Select an approved template'} />
                  </SelectTrigger>
                  <SelectContent>
                    {waTemplates.map((t) => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} · {t.language} {t.params_needed ? `· ${t.params_needed} var` : ''} {t.category ? `· ${t.category}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Only Meta-approved templates can be broadcast. The first variable auto-fills with the lead's first name.</p>
              </div>
              <div>
                <Label>Messages/Day (pacing tier)</Label>
                <Input type="number" min={1} max={100000} value={form.messages_per_day} onChange={(e) => setForm({ ...form, messages_per_day: parseInt(e.target.value) || 250 })} data-testid="campaign-messages-per-day-input" />
                <p className="text-xs text-muted-foreground mt-1">Match your WhatsApp tier (250 / 1K / 10K / 100K).</p>
              </div>
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority (1=highest, 10=lowest)</Label>
                <Input type="number" min={1} max={10} value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 5 })} data-testid="campaign-priority-input" />
              </div>
              <div>
                <Label>Min ICP Score</Label>
                <Input type="number" min={0} max={100} value={form.target_filters.min_icp_score} onChange={(e) => setForm({ ...form, target_filters: { ...form.target_filters, min_icp_score: parseInt(e.target.value) || 0 } })} data-testid="campaign-icp-score-input" />
              </div>
            </div>

            {/* Lead Status Targeting */}
            <div>
              <Label className="mb-2 block">Target Lead Statuses</Label>
              <div className="flex flex-wrap gap-2">
                {LEAD_STATUSES.map((st) => (
                  <label key={st} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={(form.target_filters.statuses || []).includes(st)}
                      onCheckedChange={() => toggleStatus(st)}
                      data-testid={`status-checkbox-${st.replace(/\s/g, '-').toLowerCase()}`}
                    />
                    <span className="text-sm">{st}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Leave all unchecked to target all statuses with a phone number</p>
            </div>

            {/* Lead Preview */}
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={handlePreviewLeads} data-testid="preview-leads-btn">
                <Target className="h-3 w-3 mr-1.5" /> Preview Matching Leads
              </Button>
              {previewCount !== null && (
                <span className="text-sm font-medium" data-testid="preview-count">
                  {previewCount} lead{previewCount !== 1 ? 's' : ''} match{previewCount === 1 ? 'es' : ''}
                </span>
              )}
            </div>

            <div>
              <Label>Campaign Objectives / Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Describe the campaign goals..." rows={2} data-testid="campaign-notes-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Market</Label>
                <select
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.target_market}
                  onChange={(e) => setForm({ ...form, target_market: e.target.value })}
                  data-testid="campaign-target-market"
                >
                  <option value="">Auto-detect per lead</option>
                  <option value="IN">India</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="SG">Singapore</option>
                  <option value="AE">UAE</option>
                </select>
              </div>
              <div>
                <Label>Industry</Label>
                <select
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  data-testid="campaign-industry"
                >
                  <option value="">Auto-detect per lead</option>
                  <option value="SaaS">SaaS / B2B Tech</option>
                  <option value="BFSI">Banking / Finance</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Construction">Construction</option>
                  <option value="D2C">D2C / Retail</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Education">Education / EdTech</option>
                  <option value="Automotive">Automotive</option>
                  <option value="Travel">Travel / Hospitality</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setPreviewCount(null); }}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="campaign-create-submit">Create Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="border rounded-lg p-4 flex items-center gap-3">
      <div className="shrink-0">{icon}</div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="border rounded-lg p-3 flex items-center gap-2">
      {icon}
      <span className="text-sm font-medium">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}
